import { useMemo } from 'react'
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization'
import { CURRENCY_OPTIONS } from '@/features/organization/constants/settingsOptions'
import { FALLBACK_CURRENCY } from '@/lib/formatters'

export interface CurrencyOption {
  value: string
  label: string
}

export interface OrgCurrency {
  /** The org's configured default currency (falls back to {@link FALLBACK_CURRENCY}). */
  currency: string
  /** Currencies the org allows, defaulting to `[currency]` when none are configured. */
  supportedCurrencies: string[]
  /** Select options for the supported currencies, labelled from the canonical list. */
  currencyOptions: CurrencyOption[]
}

/**
 * Resolves the current organization's currency configuration from
 * `settings.defaultCurrency` / `settings.supportedCurrencies`. This is the
 * single frontend source for "what currency should this org use" — replaces
 * the hardcoded 'PKR'/'USD' literals that were scattered across money surfaces.
 *
 * Reuses the cached `useCurrentOrganization` query, so it costs nothing beyond
 * the org fetch every authenticated page already makes.
 */
export function useOrgCurrency(): OrgCurrency {
  const { data: organization } = useCurrentOrganization()
  const settings = organization?.settings

  return useMemo(() => {
    const currency = settings?.defaultCurrency?.trim() || FALLBACK_CURRENCY
    const supportedCurrencies =
      settings?.supportedCurrencies && settings.supportedCurrencies.length > 0
        ? settings.supportedCurrencies
        : [currency]
    const currencyOptions: CurrencyOption[] = supportedCurrencies.map((code) => ({
      value: code,
      label: CURRENCY_OPTIONS.find((o) => o.value === code)?.label ?? code,
    }))
    return { currency, supportedCurrencies, currencyOptions }
  }, [settings?.defaultCurrency, settings?.supportedCurrencies])
}
